import {useEffect} from 'react';
import {authSlice} from '@reducers';
import {
    getRefreshTokenExpirationMs,
    isRefreshTokenExpired,
    REFRESH_TOKEN_EXPIRATION_BUFFER_MS,
} from '@utils/auth';
import {useDispatch, useSelector} from './common';

const MAX_TIMEOUT_MS = 2_147_483_647;

export const useAuthExpirationGuard = () => {
    const dispatch = useDispatch();
    const {token, refresh_token, refresh_token_expiration} = useSelector(
        state => state.auth
    );

    useEffect(() => {
        const auth = {token, refresh_token, refresh_token_expiration};
        const logoutIfExpired = (): boolean => {
            if (!isRefreshTokenExpired(auth)) {
                return false;
            }

            dispatch(authSlice.actions.logout());
            return true;
        };

        if (!token || !refresh_token || logoutIfExpired()) {
            return;
        }

        const expirationMs = getRefreshTokenExpirationMs(
            refresh_token_expiration
        );
        if (expirationMs === null) {
            return;
        }

        let timeoutId: number | null = null;

        const scheduleExpirationCheck = () => {
            const delayMs =
                expirationMs - Date.now() - REFRESH_TOKEN_EXPIRATION_BUFFER_MS;

            if (delayMs <= 0) {
                logoutIfExpired();
                return;
            }

            timeoutId = window.setTimeout(
                () => {
                    if (!logoutIfExpired()) {
                        scheduleExpirationCheck();
                    }
                },
                Math.min(delayMs, MAX_TIMEOUT_MS)
            );
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                logoutIfExpired();
            }
        };

        scheduleExpirationCheck();
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', logoutIfExpired);

        return () => {
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
            window.removeEventListener('focus', logoutIfExpired);
        };
    }, [dispatch, refresh_token, refresh_token_expiration, token]);
};
